import { useEffect, useRef } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import { arrayPath } from 'helpers/candidate_application/field-paths';
import { getErrorMessage } from 'helpers/candidate_application/form-page-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import addValidators from 'helpers/validators/apply-validators';
import { useKeyboardHints } from 'hooks/keyboard-hints';
import { useFieldArray } from 'react-hook-form';

import QuestionWrapper from 'components/Form/Questions/QuestionWrapper';
import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

// Component to render subField with keyboard hints
const SubFieldWithKeyboardHints = ({
  arrayIndex,
  subField,
  fieldName,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  candidateId,
  handlers,
  originalGetComponentProps,
  isVisible = true,
}) => {
  const keyboardHint = useKeyboardHints(subField?.type);

  // Create validation rules for the subField (skip if not visible)
  const validationRules = isVisible
    ? addValidators(
        subField.type,
        subField.min,
        subField.max,
        subField.required,
        subField.validatorName,
        subField.validationParams,
        getValues,
        fieldName,
      )
    : {};

  return (
    <QuestionWrapper
      key={fieldName}
      fieldName={fieldName}
      question={subField}
      control={control}
      register={register}
      setValue={setValue}
      trigger={trigger}
      getValues={getValues}
      error={error}
      componentMap={componentMap}
      brand={brand}
      requirementVariantStyle="text"
      gap="small"
      titleSize="small"
      keyboardHint={keyboardHint}
      getComponentProps={value =>
        originalGetComponentProps(subField, value, candidateId, handlers, brand)
      }
      validators={validationRules}
      isVisible={isVisible}
    />
  );
};

export const DynamicArrayField = ({
  question,
  control,
  register,
  setValue,
  trigger,
  getValues,
  error,
  componentMap,
  brand,
  subFields,
  arrayItemTitles,
  max,
  name,
  validators,
  candidateId,
  handlers,
  isVisible = true,
  fieldPathIndex,
  allValues = null,
  errors,
}) => {
  // Import getComponentProps directly to avoid circular dependency
  const { getComponentProps: originalGetComponentProps } = require('helpers/question-config');

  // Use props from QuestionWrapper if available, otherwise fall back to question object
  const arrayName = name || question?.code;
  const subFieldsToUse = subFields || question?.subFields;
  const arrayItemTitlesToUse = arrayItemTitles || question?.arrayItemTitles;
  const arrayStartInitialized = question?.arrayStartInitialized;

  const { fields, append, remove } = useFieldArray({
    control,
    name: arrayName,
  });

  // Track if initialization has already happened
  const initializedRef = useRef(false);

  // Handle arrayStartInitialized - initialize with one item if true and array is empty
  useEffect(() => {
    if (arrayStartInitialized && fields.length === 0 && !initializedRef.current) {
      const defaultValues = Object.fromEntries(
        subFieldsToUse.map(sf => [sf.code, sf.defaultValue ? sf.defaultValue : '']),
      );
      append(defaultValues);
      initializedRef.current = true;
    }
  }, [arrayStartInitialized, fields.length, subFieldsToUse]);

  // --- Handlers ---
  const handleAddItem = () => {
    const defaultValues = Object.fromEntries(
      subFieldsToUse.map(sf => [sf.code, sf.defaultValue !== undefined ? sf.defaultValue : '']),
    );
    append(defaultValues);
  };

  const handleRemoveItem = arrayIndex => {
    remove(arrayIndex);
  };

  return (
    <div className="d-flex flex-column u-gap-large">
      {/* <h3 className="font-semibold text-lg">
        {question?.title || question?.text || question?.label}
      </h3> */}

      {fields?.map((item, arrayIndex) => (
        <div
          key={item.id}
          className="d-flex flex-column u-gap-large u-padding u-border u-border-radius">
          <div className="d-flex flex-row justify-content-between">
            <span className="primary-text u-font-size-x-large u-font-weight-500">
              {question?.arrayItemTitles || question?.title || 'Item'} #{arrayIndex + 1}
            </span>
            {arrayIndex > 0 && (
              <IconComponent
                icon={Delete02Icon}
                type="error"
                size="small"
                noBackground
                className="u-flex-align-self-end"
                onClick={() => handleRemoveItem(arrayIndex)}
              />
            )}
          </div>
          {subFieldsToUse.map(subField => {
            // Check if subField has conditions
            let conditionsMet = true;
            if (subField?.conditions) {
              // Merge current form values with allValues to ensure array structures are available
              const currentFormValues = getValues();
              const valuesForCondition = { ...(allValues || {}), ...currentFormValues };
              conditionsMet = evaluateFieldConditions(subField.conditions, valuesForCondition, {
                parentCode: arrayName,
                arrayIndex: arrayIndex,
                fieldPathIndex,
              });
            }

            // Skip rendering if conditions aren't met
            if (!conditionsMet) {
              return null;
            }

            const fullFieldName = arrayPath(arrayName, arrayIndex, subField.code);

            return (
              <SubFieldWithKeyboardHints
                key={fullFieldName}
                arrayIndex={arrayIndex}
                fieldName={fullFieldName}
                subField={subField}
                control={control}
                register={register}
                setValue={setValue}
                trigger={trigger}
                getValues={getValues}
                error={getErrorMessage(errors[arrayName]?.[arrayIndex]?.[subField.code])}
                brand={brand}
                candidateId={candidateId}
                componentMap={componentMap}
                handlers={handlers}
                originalGetComponentProps={originalGetComponentProps}
                isVisible={isVisible && conditionsMet}
              />
            );
          })}
        </div>
      ))}
      <div className="u-flex-align-self-center">
        <Button
          type="secondary"
          disabled={max && fields?.length >= max}
          square
          onClick={handleAddItem}>
          Add {arrayItemTitlesToUse || question?.arrayItemTitles || question?.itemLabel || 'Item'}
        </Button>
      </div>
    </div>
  );
};
